import { getApiClient } from '@src/shared/types/api-client'
import type { DatabasePendingApprovalCount } from '@src/tools/anonymizer/types/Constants'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

export const dashboardKeys = {
  errorCount: () => [{ scope: 'dashboard', entity: 'errorCount' }] as const,
  anonymizerSummary: () => [{ scope: 'dashboard', entity: 'anonymizerSummary' }] as const,
  commandSummary: () => [{ scope: 'dashboard', entity: 'commandSummary' }] as const,
}

const getExceptionsCount = async () => {
  const apiClient = getApiClient()
  const response = await apiClient.post<{ count: number }>('ExceptionLogs/Count', {
    statuses: ['new'],
  })
  return response.data.count
}

export function useExceptionsCounts(): [number, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: dashboardKeys.errorCount(),
    queryFn: getExceptionsCount,
    placeholderData: 0,
  })

  return [data ?? 0, isFetching]
}

const getDatabaseSummaries = async (): Promise<DatabasePendingApprovalCount[]> => {
  const apiClient = getApiClient()
  const response = await apiClient.get<DatabasePendingApprovalCount[]>('databases/pendingApprovalCounts')
  return response.data
}

export function useAnonymizerSummary(): [number, number, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: dashboardKeys.anonymizerSummary(),
    queryFn: getDatabaseSummaries,
    placeholderData: [],
  })

  const dbCount = data?.length ?? 0
  const pendingApprovalCount = useMemo(
    () => (data ?? []).reduce((sum, db) => sum + db.totalPendingApproval, 0),
    [data],
  )

  return [dbCount, pendingApprovalCount, isFetching]
}

const getDisabledTimerCount = async (): Promise<number> => {
  const apiClient = getApiClient()
  const response = await apiClient.get<{ count: number }>('FunctionApps/DisabledTimerCount')
  return response.data.count
}

export function useCommandSummary(): [number, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: dashboardKeys.commandSummary(),
    queryFn: getDisabledTimerCount,
    placeholderData: 0,
  })
  return [data ?? 0, isFetching]
}
